import { ParsedUrlQuery } from 'querystring';
import { safeJsonLd } from '@c/StructuredData';
import { GetServerSideProps } from 'next';
import LandingPageHeader from '@c/landing/LandingPageHeader';
import HeroBanner from '@c/landing/HeroBanner';
import WhyGearFocus from '@c/landing/WhyGearFocus';
import AboutCelebrityBanner from '@c/landing/AboutCelebrityBanner';
import ListingCategories from '@c/landing/ListingCategories';
import HowItWorks from '@c/landing/HowItWorks';
import FAQSection from '@c/landing/FAQSection';
import PromoBanner from '@c/landing/PromoBanner';
import SalesTableSection from '@c/landing/SalesTableSection';
import TestimonialSection from '@c/landing/TestimonialSection';
import LandingPageFooter from '@c/landing/LandingPageFooter';
import GearBagSection from '@c/landing/GearBagSection';
import SEO from '@c/SEO';
import Head from 'next/head';

export default function MasterPage() {
  return (
    <>
      <SEO
        title="Wedding Photography Gear - Endorsed by Vanessa Joy"
        description="Shop wedding camera gear endorsed by Vanessa Joy. Verified marketplace for photographers to buy & sell safely with 48-hour buyer protection and held payments."
        ogImage="https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/vanessa-joy-hero-banner.png"
        keywords={[
          'wedding photography gear',
          'wedding camera equipment',
          'Vanessa Joy',
          'buy wedding photography gear',
          'sell camera gear',
          'wedding photography marketplace',
          'used wedding camera gear',
          'professional wedding cameras',
          'wedding lenses',
          'Canon Explorer of Light',
          'Profoto Legend'
        ]}
      />

      <Head>
        {/* Vanessa Joy Person Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "Person",
              "name": "Vanessa Joy",
              "jobTitle": "Wedding and Portrait Photographer",
              "description": "Canon Explorer of Light, Profoto Legend of Light, Internationally Acclaimed Wedding and Portrait Photographer",
              "url": "https://gearfocus.com/shop-seller/VanessaJoy",
              "sameAs": [
                "https://www.vanessajoy.com",
                "https://www.instagram.com/vanessajoy",
                "https://www.youtube.com/vanessajoy"
              ],
              "brand": [
                {
                  "@type": "Brand",
                  "name": "Canon"
                },
                {
                  "@type": "Brand",
                  "name": "Profoto"
                }
              ],
              "award": [
                "Canon Explorer of Light",
                "Profoto Legend of Light"
              ],
              "knowsAbout": [
                "Wedding Photography",
                "Portrait Photography",
                "Professional Camera Equipment",
                "Photography Education"
              ]
            })
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Wedding Photography Gear Marketplace - Endorsed by Vanessa Joy",
              "description": "Shop wedding camera gear endorsed by Vanessa Joy. Verified marketplace for photographers to buy & sell safely with 48-hour buyer protection and held payments.",
              "url": "https://gearfocus.com/lp/wedding-photography-gear-marketplace",
              "specialty": "Wedding Photography Equipment",
              "about": {
                "@type": "Thing",
                "name": "Wedding Photography Equipment"
              },
              "mentions": {
                "@type": "Person",
                "name": "Vanessa Joy"
              }
            })
          }}
        />

        {/* BreadcrumbList Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://gearfocus.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Landing Pages",
                  "item": "https://gearfocus.com/lp"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Wedding Photography Gear",
                  "item": "https://gearfocus.com/lp/wedding-photography-gear-marketplace"
                }
              ]
            })
          }}
        />

        {/* ItemList Schema for Vanessa's Gear Bag */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: safeJsonLd({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "name": "Vanessa Joy's Wedding Photography Gear Bag",
              "description": "Professional camera equipment used by Vanessa Joy for wedding photography",
              "itemListElement": [
                {
                  "@type": "Product",
                  "position": 1,
                  "name": "Canon EOS R5 Mark II",
                  "description": "Professional mirrorless camera for wedding photography",
                  "brand": {
                    "@type": "Brand",
                    "name": "Canon"
                  },
                  "url": "https://gearfocus.com/m/canon-eos-r5-mark-ii",
                  "image": "https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/Canon-R5-Mark-II.png",
                  "category": "Camera"
                },
                {
                  "@type": "Product",
                  "position": 2,
                  "name": "Canon RF 28-70 F2 L USM",
                  "description": "Professional portrait and wedding lens with F2 aperture",
                  "brand": {
                    "@type": "Brand",
                    "name": "Canon"
                  },
                  "url": "https://gearfocus.com/m/canon-rf-28-70mm-f2-l-usm",
                  "image": "https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/Canon-RF-28-70-F2-L-USM.png",
                  "category": "Lens"
                },
                {
                  "@type": "Product",
                  "position": 3,
                  "name": "Canon RF 70-200 F2.8 L IS USM Z",
                  "description": "Professional telephoto zoom lens for ceremonies and portraits",
                  "brand": {
                    "@type": "Brand",
                    "name": "Canon"
                  },
                  "url": "https://gearfocus.com/m/canon-rf-70-200mm-f28-l-is-usm",
                  "image": "https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/Canon-RF-70-200-F2.8-L-IS-USM-Z.png",
                  "category": "Lens"
                },
                {
                  "@type": "Product",
                  "position": 4,
                  "name": "Profoto B30 Off Camera Flash",
                  "description": "Professional off-camera flash for wedding photography",
                  "brand": {
                    "@type": "Brand",
                    "name": "Profoto"
                  },
                  "url": "https://gearfocus.com/m/profoto-b30-off-camera-flash",
                  "image": "https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/Profoto-B30-Off-Camera-Flash.png",
                  "category": "Lighting"
                }
              ]
            })
          }}
        />
      </Head>

      <LandingPageHeader />

      <HeroBanner
        backgroundImageUrlWeb="https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/vanessa-joy-hero-banner.png"
        backgroundImageUrlMobile="https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/vanessa-joy-hero-banner-mobile.png"
        title="Wedding Photographers:"
        headline="Stop Overpaying for Camera Gear — Buy and Sell Safely."
        subheadline="GearFocus is the only verified marketplace designed specifically for creators like you, endorsed by wedding pro,"
        highlightName="Vanessa Joy"
        ctaPrimaryLink={{url: "/sell", target: "_self"}}
        ctaPrimaryLabel="List Gear for Free"
        ctaSecondaryLink={{url: "https://gearfocus.com/shop-seller/VanessaJoy", target: "_self"}}
        ctaSecondaryLabel="Shop Vanessa's Store"
        ctaBubble="https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/vanessa-joy-profile-bubble.png"
        ctaBubbleAlt="Vanessa Joy - Canon Explorer of Light and wedding photography expert"
        ratingText="4.7 Excellent on"
        ratingImageUrl="https://gearfocus.b-cdn.net/landings/rating-star.svg"
        ratingImageAlt="4.7 out of 5 star rating from verified customer reviews"
        trustpilotLogoUrl="https://gearfocus.b-cdn.net/landings/trustpilot-logo.svg"
        trustpilotLogoAlt="Trustpilot - Independent review platform"
        trustStatement="Join 10,000+ photographers trusting us today!"
      />

      <WhyGearFocus />

      <AboutCelebrityBanner
        heading={`I Don't Risk My Clients—or My Gear.`}
        quote={`"This is what our community has been missing—an honest, safe space where photographers take care of each other."`}
        name="Vanessa Joy"
        title={`Canon Explorer of Light, Profoto Legend of Light Internationally Acclaimed Wedding and Portrait Photographer`}
        imageUrl="https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/vanessa-joy-shooting.png"
        imageAlt="Vanessa Joy, Canon Explorer of Light, photographing a wedding with professional Canon R5 camera and RF lens"
      />

      <ListingCategories
        title= "Essential Wedding Photography Gear to Capture Every Moment"
        items={[
          { src: 'https://gearfocus.b-cdn.net/landings/wedding-cameras.svg', alt: 'Shop wedding cameras', url: '/c/digital-cameras?useCase=Weddings' },
          { src: 'https://gearfocus.b-cdn.net/landings/wedding-lenses.svg', alt: 'Shop wedding lenses', url: '/c/photo-video-lenses?useCase=Weddings' },
          { src: 'https://gearfocus.b-cdn.net/landings/wedding-video.svg', alt: 'Shop wedding video gear', url: '/c/video-camcorders?useCase=Weddings' },
          { src: 'https://gearfocus.b-cdn.net/landings/wedding-drones.svg', alt: 'Shop wedding drones', url: '/c/drones-aerial-imaging?useCase=Weddings' },
          { src: 'https://gearfocus.b-cdn.net/landings/wedding-accessories.svg', alt: 'Shop wedding accessories', url: '/c/photo-video-accessories?useCase=Weddings' },
        ]}  
      />

      <HowItWorks />

      <FAQSection
        faqs={
          [
            {
              question: 'Is GearFocus really safer than eBay or Facebook Marketplace?',
              answer:
                "Yes. Every seller is verified, your payment is held by GearFocus until you sign off on receipt or your 48-hour verification window ends, and if the gear isn't as described you can return it at no charge.",
            },
            {
              question: 'How much does it cost to sell on GearFocus?',
              answer:
                'Listings are free, and we charge the industry’s lowest total fee (8.5%) only when your gear sells. No hidden fees or surprise charges.',
            },
            {
              question: 'How do I know I’ll actually get paid?',
              answer:
                "GearFocus holds the buyer's payment and releases it to the seller once the buyer confirms receipt or the 48-hour verification window closes with no dispute.",
            },
            {
              question: 'Can I return something if it’s not as described?',
              answer:
                'Yes. Buyers have 48 hours to inspect the gear. If it doesn’t match the listing, we’ll cover the return and refund you in full.',
            },
            {
              question: 'How long does it take to get paid once my item has sold?',
              answer:
                'Payments are released when the buyer confirms delivery, or automatically 48 hours after confirmed delivery if no issue is raised.',
            },
            {
              question: 'Is GearFocus just for wedding photographers?',
              answer:
                'Nope! It’s built for all creators—but this page is tailored specifically to wedding pros, since we know how tight your margins and timelines can be.',
            }
          ]
        }
      />

      <PromoBanner
        videoUrl="/landings/wedding-slideshow/vj-loop.mp4"
        videoPoster="https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/vanessa-promo.jpg"
        mainImage="https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/vanessa-promo.jpg"
        mainImageAlt="Vanessa Joy wedding photography work - behind the scenes and portfolio examples"
        cornerImage="https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/girl-with-camera.jpg"
        cornerImageAlt="Wedding photographer shooting with professional Canon camera and lens"
        link="https://bit.ly/gearfocustraffic"
        linkText="Start Learning with Vanessa Today"
        header="Want to Shoot Weddings Like Vanessa Joy?"
        subtext="Join Vanessa's Photo Insiders Today"
      />

      <SalesTableSection
        columns={[{title: 'B&H'}, {title: 'eBay'}, {title: 'MPB'}, {title: 'GearFocus', color: '#FFFFFF', bg: '#ED7523'}]}
        rows={[
          {
            product: 'Canon EF 70-200mm F2.8 L',
            bh: '$440',
            ebay: '$907',
            mpb: '$620',
            gearFocus: '$959',
          },
          {
            product: 'Canon EOS R5',
            bh: '$1,390',
            ebay: '$2,147',
            mpb: '$1,500',
            gearFocus: '$2,271',
          },
          {
            product: 'Profoto B10X',
            bh: 'N/A',
            ebay: '$1,332',
            mpb: '$790',
            gearFocus: '$1,409',
          },
          {
            product: 'Manfrotto MT055CXPRO3 Tripod',
            bh: 'N/A',
            ebay: '$248',
            mpb: '$90',
            gearFocus: '$262',
          }
        ]}
      />

      <TestimonialSection
        backgroundImage="https://gearfocus.b-cdn.net/landings/paul-skorupskas-bg.jpg"
        quote="Truly the most amazing customer service... and gave me an amazing deal on a high quality camera."
        author="Kyle G."
        platform="Trustpilot"
        platformLogo={{
          src: 'https://gearfocus.b-cdn.net/landings/trustpilot-logo-white.svg',
          alt: 'Trustpilot logo',
          width: 100,
          height: 100,
        }}
      />

      <GearBagSection
        header="What's in Vanessa's Gear Bag?"
        items={[
          {
            title: "Canon EOS R5 Mark II",
            url: "https://gearfocus.com/m/canon-eos-r5-mark-ii",
            src: "https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/Canon-R5-Mark-II.png"
          }, {  
            title: "Canon RF 28-70 F2 L USM",
            url: "https://gearfocus.com/m/canon-rf-28-70mm-f2-l-usm",
            src: "https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/Canon-RF-28-70-F2-L-USM.png"
          }, {
            title: "Canon RF 70-200 F2.8 L IS USM Z",
            url: "https://gearfocus.com/m/canon-rf-70-200mm-f28-l-is-usm",
            src: "https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/Canon-RF-70-200-F2.8-L-IS-USM-Z.png"
          }, {
            title: "Profoto B30 Off Camera Flash",
            url: "https://gearfocus.com/m/profoto-b30-off-camera-flash",
            src: "https://gearfocus.b-cdn.net/landings/d3XofynJnEbNGdQO4rmAPDN4wnH2/Profoto-B30-Off-Camera-Flash.png"
          },
        ]}
        footer="Vanessa's essential wedding photography kit includes the Canon R5 Mark II, RF 28-70mm F2 L, and RF 70-200mm F2.8 L IS lenses—perfect for portraits, ceremonies, and low-light shots. She also trusts the Profoto B30 Off Camera Flash, Manfrotto MT055 Tripod, SanDisk Extreme Pro memory cards, and a reliable backup camera."
      />

      <LandingPageFooter />
    </>
  );
}

MasterPage.getLayout = function getLayout(page: React.ReactElement) {
  return <div className="min-h-screen bg-white">{page}</div>;
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=60, stale-while-revalidate=300'
  );

  return {
    props: {
      
    },
  };
};
